import { useState, useEffect, useCallback, useRef } from "react";

/**
 * Custom hook for state that persists to localStorage.
 * Automatically saves state changes and restores on mount.
 *
 * @param key - localStorage key to store the state under
 * @param defaultValue - Default value if no saved state exists
 * @returns Tuple of [state, setState, clearState]
 *
 * @example
 * ```tsx
 * const [step, setStep, clearStep] = usePersistedState('demo-flow-step', 1);
 * ```
 */
export function usePersistedState<T>(
  key: string,
  defaultValue: T
): [T, (value: T | ((prev: T) => T)) => void, () => void] {
  const defaultValueRef = useRef(defaultValue);
  defaultValueRef.current = defaultValue;

  // Initialize state from localStorage or use default
  const [state, setState] = useState<T>(() => {
    if (typeof window === "undefined") {
      return defaultValue;
    }

    try {
      const saved = localStorage.getItem(key);
      if (saved === null) {
        return defaultValue;
      }
      return JSON.parse(saved) as T;
    } catch (error) {
      console.warn(`Failed to parse localStorage key "${key}":`, error);
      return defaultValue;
    }
  });

  // Save to localStorage whenever state changes
  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    try {
      localStorage.setItem(key, JSON.stringify(state));
    } catch (error) {
      console.error(`Failed to save to localStorage key "${key}":`, error);
    }
  }, [key, state]);

  // Clear function to remove from localStorage and reset to default
  const clearState = useCallback(() => {
    if (typeof window === "undefined") {
      return;
    }

    try {
      localStorage.removeItem(key);
      setState(defaultValueRef.current);
    } catch (error) {
      console.error(`Failed to clear localStorage key "${key}":`, error);
    }
  }, [key]);

  return [state, setState, clearState];
}
